/**
 * Drives the Monkey Worlds screen. The platformer + world map live inside a WebView game runtime;
 * this hook owns the economy bridge: it boots the runtime with the day's packed progress + local
 * stars on `ready`, reports each cleared level to the server, and syncs coins from the SERVER
 * response only (never a client-computed total). The server decides every award + the daily cap,
 * so a spoofed clear can't mint coins.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApiClient, extractErrorMessage } from '../../../helpers/apiClient.helper';
import { useSoundStore } from '../../shared/stores/soundStore';
import { useEconomyStore } from '../../shared/stores/economyStore';
import { useAnalytics } from '../../analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../analytics/events';
import { startMonkeyWorldsRun, recordMonkeyWorldsLevelClear } from '../helpers/monkeyWorldsApi';
import { buildMonkeyWorldsGameHtml } from '../helpers/monkeyWorldsGameHtml';
import {
  readMonkeyWorldsStars,
  saveMonkeyWorldsStars,
  type MonkeyWorldsStarMap,
} from '../helpers/monkeyWorldsStarsStorage.helper';
import {
  MONKEY_WORLDS_GAME_KEY,
  MONKEY_WORLDS_DAILY_COIN_CAP,
  type MonkeyWorldsAudioPayload,
  type MonkeyWorldsBridgeMessage,
  type MonkeyWorldsStartPayload,
} from '../constants/monkeyWorlds.constants';

type Status = 'loading' | 'ready' | 'error';
const TOAST_MS = 2200;

/**
 * Intent-based host surface (not raw JS injection) so the same hook drives both the native WebView
 * and the web iframe, and stays unit-testable without either. The platform hosts implement this.
 */
export interface MonkeyWorldsWebViewRef {
  applyStart: (payload: MonkeyWorldsStartPayload) => void;
  applyAudio: (payload: MonkeyWorldsAudioPayload) => void;
  reload: () => void;
}

export function useMonkeyWorldsGame(options?: { insetTop?: number }) {
  const insetTop = options?.insetTop ?? 0;
  const apiClient = useApiClient();
  const syncCoins = useEconomyStore((s) => s.syncCoins);
  // Mute is the app-wide setting (AC-19); the runtime holds a mirror, this is the source of truth.
  const isMuted = useSoundStore((s) => s.isMuted);
  const setMuted = useSoundStore((s) => s.setMuted);
  // The value the runtime is known to hold. Without it, inject -> post -> store -> effect -> inject
  // loops forever.
  const mutedRef = useRef(isMuted);
  const { track } = useAnalytics();

  const html = useMemo(() => buildMonkeyWorldsGameHtml(), []);
  const webViewRef = useRef<MonkeyWorldsWebViewRef | null>(null);
  const startedTrackedRef = useRef(false);
  const progressRef = useRef(0); // last known packed progress (server-confirmed)

  const [status, setStatus] = useState<Status>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [coinsLeftToday, setCoinsLeftToday] = useState(MONKEY_WORLDS_DAILY_COIN_CAP);
  const [capReached, setCapReached] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // Session tally (this play, not all-time) — feeds the exit funnel event.
  const [levelsCleared, setLevelsCleared] = useState(0);
  const [sessionCoins, setSessionCoins] = useState(0);

  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
  }, []);
  const flashToast = useCallback((message: string) => {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), TOAST_MS);
  }, []);

  const bindWebView = useCallback((ref: MonkeyWorldsWebViewRef | null) => { webViewRef.current = ref; }, []);

  /** Fetch today's economy + progress and boot the runtime's world map. Called on `ready`. */
  const start = useCallback(async () => {
    setStatus('loading');
    setErrorMessage(null);
    let stars: MonkeyWorldsStarMap = {};
    try { stars = await readMonkeyWorldsStars(); } catch { /* stars are cosmetic — fail open */ }
    try {
      const cfg = await startMonkeyWorldsRun(apiClient);
      progressRef.current = cfg.worldProgress;
      setCoinsLeftToday(cfg.coinsLeftToday);
      setCapReached(cfg.capReached);
      setStatus('ready');
      webViewRef.current?.applyStart({ worldProgress: cfg.worldProgress, stars, insetTop, muted: mutedRef.current });
    } catch {
      // Offline fallback — boot with the last known progress so the game is always playable
      // (coins won't bank; the runtime's session progress still opens doors locally).
      setStatus('ready');
      webViewRef.current?.applyStart({ worldProgress: progressRef.current, stars, insetTop, muted: mutedRef.current });
    }
  }, [apiClient, insetTop]);

  const handleClear = useCallback(async (msg: Extract<MonkeyWorldsBridgeMessage, { type: 'level-clear' }>) => {
    let coinsAwarded = 0; // stays 0 if the server call fails (offline / staging without routes)
    // Stars persist locally regardless of the network — cosmetic, per-device (spec Non-Goals).
    void saveMonkeyWorldsStars(msg.world, msg.slot, msg.stars);
    try {
      const res = await recordMonkeyWorldsLevelClear(apiClient, {
        world: msg.world,
        slot: msg.slot,
        stars: msg.stars,
        timeTicks: msg.timeTicks,
      });
      coinsAwarded = res.coinsAwarded;
      // Coins come from the server only — anonymous users get no `coins`, so nothing is synced.
      if (typeof res.coins === 'number') syncCoins(res.coins);
      progressRef.current = res.worldProgress;
      setCoinsLeftToday(res.coinsLeftToday);
      setCapReached(res.capReached);
    } catch (err) {
      // Gameplay never blocks on the network — the runtime already advanced; just note it.
      flashToast(extractErrorMessage(err, 'Coins will catch up'));
    } finally {
      setLevelsCleared((n) => n + 1);
      setSessionCoins((c) => c + coinsAwarded);
      // Progression signal — fire regardless of the server result so the drop-off funnel stays accurate.
      track(ANALYTICS_EVENTS.GAME_LEVEL_CLEARED, {
        game_key: MONKEY_WORLDS_GAME_KEY,
        world: msg.world,
        level: msg.slot,
        stars: msg.stars,
        time_ticks: msg.timeTicks,
        coins_awarded: coinsAwarded,
      });
    }
  }, [apiClient, syncCoins, flashToast, track]);

  const onMessage = useCallback((event: { nativeEvent: { data: string } }) => {
    let msg: MonkeyWorldsBridgeMessage;
    try { msg = JSON.parse(event.nativeEvent.data); } catch { return; }
    switch (msg.type) {
      case 'ready':
        if (!startedTrackedRef.current) {
          startedTrackedRef.current = true;
          track(ANALYTICS_EVENTS.GAME_STARTED, { game_key: MONKEY_WORLDS_GAME_KEY });
        }
        void start();
        break;
      case 'level-clear':
        void handleClear(msg);
        break;
      case 'level-failed':
        track(ANALYTICS_EVENTS.GAME_LEVEL_FAILED, {
          game_key: MONKEY_WORLDS_GAME_KEY,
          world: msg.world,
          level: msg.slot,
        });
        break;
      case 'world-complete':
        track(ANALYTICS_EVENTS.GAME_COMPLETED, {
          game_key: MONKEY_WORLDS_GAME_KEY,
          world: msg.world,
          reason: 'world',
        });
        break;
      case 'audio':
        // Came from the game — record it as already-applied so the effect below doesn't echo it back.
        mutedRef.current = msg.muted;
        setMuted(msg.muted);
        break;
      case 'load-error':
        setErrorMessage('Could not load the game');
        setStatus('error');
        break;
      default:
        break;
    }
  }, [start, handleClear, track, setMuted]);

  // App-side mute changes (settings, or a late store hydration) reach the runtime exactly once.
  useEffect(() => {
    if (status !== 'ready') return;
    if (mutedRef.current === isMuted) return; // already applied, or it originated in the game
    mutedRef.current = isMuted;
    webViewRef.current?.applyAudio({ muted: isMuted });
  }, [isMuted, status]);

  const retry = useCallback(() => {
    setStatus('loading');
    setErrorMessage(null);
    webViewRef.current?.reload(); // runtime re-posts `ready` → start() runs again
  }, []);

  /** Back pressed — close the session funnel; the view navigates away right after. */
  const confirmExit = useCallback(() => {
    track(ANALYTICS_EVENTS.GAME_COMPLETED, {
      game_key: MONKEY_WORLDS_GAME_KEY,
      level: levelsCleared,
      coins_awarded: sessionCoins,
      reason: 'exit',
    });
  }, [track, levelsCleared, sessionCoins]);

  return {
    status,
    isLoading: status === 'loading',
    isError: status === 'error',
    errorMessage,
    html,
    coinsLeftToday,
    capReached,
    toast,
    levelsCleared,
    sessionCoins,
    bindWebView,
    onMessage,
    retry,
    confirmExit,
  };
}

export type UseMonkeyWorldsGame = ReturnType<typeof useMonkeyWorldsGame>;
